"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Checkbox = _interopRequireDefault(require("../Checkbox/Checkbox"));
var _TableCell = _interopRequireDefault(require("./TableCell"));
var _TableContext = require("./internal/TableContext");
var _EDSContext = require("../EDSContext/EDSContext");
var _useWidth = _interopRequireDefault(require("../useWidth"));
var _withWidth = require("../withWidth/withWidth");
var _classnames = _interopRequireDefault(require("classnames"));
var _styles = _interopRequireDefault(require("../styles"));
var _TableRow = _interopRequireDefault(require("@mui/material/TableRow"));
var _excluded = ["classes", "children", "edsContext", "parentRowGroup", "rowData"];
var styles = function styles(theme) {
  return {
    // Necessary style overrides are set here
    // See Material UI documentation for available CSS classes to override
    root: {
      height: '3rem',
      '&$hover:hover': {
        backgroundColor: theme.palette.grey[250]
      }
    },
    checkboxColumn: {
      width: '1px',
      '&.no-selection-mobile-expansion': {
        display: 'none !important'
      }
    },
    selectionCheckbox: {
      // had to use !important here because the checkbox defines margin in 'root',
      // and because of the way the stylesheets are ordered so this override is always
      // secondary
      margin: '0 !important'
    },
    hover: {},
    selected: {}
  };
};

/**
 * Table component for displaying a row
 */
var TableRow = /*#__PURE__*/_react.default.forwardRef(function (props, ref) {
  var classes = props.classes,
    children = props.children,
    edsContext = props.edsContext,
    parentRowGroup = props.parentRowGroup,
    rowData = props.rowData,
    rest = (0, _objectWithoutPropertiesLoose2.default)(props, _excluded);
  var classOverrides = (0, _extends2.default)({
    root: classes.root,
    hover: classes.hover,
    selected: classes.selected
  }, classes);
  var _useTableContext = (0, _TableContext.useTableContext)(),
    rowSelectionEnabled = _useTableContext.rowSelectionEnabled,
    rowSelectionOptions = _useTableContext.rowSelectionOptions,
    selectedRowIds = _useTableContext.selectedRowIds,
    onSelectionUpdated = _useTableContext.onSelectionUpdated,
    globalSelection = _useTableContext.globalSelection,
    tableLayout = _useTableContext.tableLayout;

  // call up to the parent table to update the selection state of a single row
  var onRowSelectionChange = function onRowSelectionChange(e) {
    onSelectionUpdated(e.target.checked, rowData);
  };

  // call up to the parent table to update the global selection state
  var onGlobalSelectionChange = function onGlobalSelectionChange() {
    // if some or all of the rows are selected, deselect everything; otherwise
    // select all of them
    onSelectionUpdated(globalSelection === _TableContext.TableProvider.ROW_SELECTION_NONE);
  };
  var checkboxCell;
  var tableWidth = (0, _useWidth.default)();
  var hideSelectAll = rowSelectionOptions == null ? void 0 : rowSelectionOptions.hideSelectAll;
  if (rowSelectionEnabled && parentRowGroup) {
    // add a checkbox cell to the beginning of each body row
    if (parentRowGroup === 'tablebody') {
      var isMobile = tableLayout ? (0, _withWidth.isWidthDown)(tableLayout.breakpoint, tableWidth, true) : false;
      if (rowData && rowData.id && !rowData.selectionDisabled) {
        // look at context data to determine if the checkbox is checked
        var checked = selectedRowIds.has(rowData.id);
        var label = edsContext.formatMessage(checked ? 'component.Table.unselectRow' : 'component.Table.selectRow');
        checkboxCell = /*#__PURE__*/_react.default.createElement(_TableCell.default, {
          className: (0, _classnames.default)('selection-column', classes.checkboxColumn)
        }, /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
          checked: checked,
          onChange: onRowSelectionChange,
          classes: {
            root: classes.selectionCheckbox,
            focused: classes.selectionCheckbox
          },
          inputProps: {
            'aria-label': label
          }
        }));
      } else {
        // no id supplied for this row, so it doesn't get a checkbox
        checkboxCell = /*#__PURE__*/_react.default.createElement(_TableCell.default, {
          className: (0, _classnames.default)(classes.checkboxColumn, {
            'no-selection-mobile-expansion': isMobile && tableLayout.variant === 'expansionPanels'
          }),
          content: ""
        });
      }

      // add a select-all checkbox to checkbox column header
    } else if (parentRowGroup === 'tablehead') {
      checkboxCell = /*#__PURE__*/_react.default.createElement(_TableCell.default, {
        className: classes.checkboxColumn
      }, !hideSelectAll && /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
        indeterminate: globalSelection === _TableContext.TableProvider.ROW_SELECTION_SOME,
        checked: globalSelection === _TableContext.TableProvider.ROW_SELECTION_ALL,
        onChange: onGlobalSelectionChange,
        classes: {
          root: classes.selectionCheckbox,
          focused: classes.selectionCheckbox
        },
        inputProps: {
          'aria-label': edsContext.formatMessage(globalSelection === _TableContext.TableProvider.ROW_SELECTION_NONE ? 'component.Table.selectAllRows' : 'component.Table.unselectAllRows')
        }
      }));
    }
  }
  return /*#__PURE__*/_react.default.createElement(_TableRow.default, (0, _extends2.default)({}, rest, {
    classes: classOverrides,
    ref: ref
  }), checkboxCell && checkboxCell, children);
});
TableRow.muiName = 'TableRow';
TableRow.displayName = 'TableRow';
TableRow.propTypes = {
  /**
   * Should be valid `<tr>`children such as `TableCell`.
   */
  children: _propTypes.default.node.isRequired,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: _propTypes.default.object,
  /**
   * The component used for the root node. Either a string to use a DOM element or a component.
   */
  component: _propTypes.default.elementType,
  /**
   * If `true`, the table row will shade on hover.
   */
  hover: _propTypes.default.bool,
  /**
   * If `true`, the table row will have the selected shading.
   */
  selected: _propTypes.default.bool,
  /**
   * 	The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.object, _propTypes.default.bool])), _propTypes.default.func, _propTypes.default.object]),
  /**
   * The data associated with this row. If row selection is enabled, this prop is required,
   * and must include an `id` attribute that uniquely identifies the row within the
   * table.
   *
   * The object may optionally include a `rowSelectionDisabled` attribute. If `true`, selection
   * will be disabled for that row.
   */
  rowData: _propTypes.default.object,
  /**
   * @ignore
   */
  parentRowGroup: _propTypes.default.string,
  /**
   * @ignore
   */
  edsContext: _propTypes.default.object
};

// If default props are needed, they must be set here
TableRow.defaultProps = {
  component: 'tr',
  hover: false,
  selected: false
};
var _default = (0, _EDSContext.withEDSContext)((0, _styles.default)(styles)(TableRow));
exports.default = _default;